"use client";

import { useRef, useState } from "react";
import { checkResumeAts, AiToolError, type AtsCheckResult, type AtsFinding } from "@/lib/platform/aiTools";

const CATEGORY_LABELS: Record<AtsFinding["category"], string> = {
  keywords: "🔑 Keywords",
  formatting: "🎨 Formatting",
  structure: "🏗️ Structure",
  impact: "📈 Impact",
};

const SEVERITY_ORDER: Record<AtsFinding["severity"], number> = { high: 0, medium: 1, low: 2 };

const SEVERITY_STYLE: Record<AtsFinding["severity"], string> = {
  high: "border-rose-200/80 bg-rose-50/60 dark:border-rose-800/60 dark:bg-rose-500/5",
  medium: "border-amber-200/80 bg-amber-50/60 dark:border-amber-800/60 dark:bg-amber-500/5",
  low: "border-slate-200/80 bg-white/80 dark:border-slate-800 dark:bg-[#0f172a]/80",
};

function scoreColor(score: number): string {
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#f59e0b";
  return "#f43f5e";
}

export default function AtsCheckerTool() {
  const [mode, setMode] = useState<"file" | "paste">("file");
  const [file, setFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AtsCheckResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function pickFile(f: File) {
    if (!/\.(pdf|docx)$/i.test(f.name)) {
      setError("Only PDF or DOCX files are supported.");
      return;
    }
    setError(null);
    setFile(f);
  }

  async function analyze() {
    if (mode === "file" && !file) {
      setError("Choose a PDF or DOCX file first.");
      return;
    }
    if (mode === "paste" && !pastedText.trim()) {
      setError("Paste your resume text first.");
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    try {
      const res = await checkResumeAts({
        file: mode === "file" ? file ?? undefined : undefined,
        resumeText: mode === "paste" ? pastedText : undefined,
        jobTitle: jobTitle.trim() || undefined,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof AiToolError ? e.message : "Something went wrong analyzing your resume. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const sortedFindings = result ? [...result.findings].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]) : [];

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-6 backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode("file")}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
              mode === "file" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            Upload File
          </button>
          <button
            type="button"
            onClick={() => setMode("paste")}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
              mode === "paste" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            Paste Text
          </button>
        </div>

        <div className="mt-4">
          {mode === "file" ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files[0]) pickFile(e.dataTransfer.files[0]);
              }}
              className={`flex h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed text-center text-sm text-slate-500 dark:text-slate-400 ${
                dragOver ? "border-violet-400 bg-violet-50/50 dark:bg-violet-500/5" : "border-slate-300 dark:border-slate-700"
              }`}
            >
              {file ? (
                <>
                  <span className="text-2xl">📄</span>
                  <span className="font-medium text-slate-700 dark:text-slate-200">{file.name}</span>
                  <span className="text-xs">Click to replace</span>
                </>
              ) : (
                <>
                  <span className="text-2xl">⬆️</span>
                  <span>Click or drag a PDF/DOCX resume here</span>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.docx"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) pickFile(e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>
          ) : (
            <textarea
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              rows={8}
              placeholder="Paste your resume text here…"
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
            />
          )}
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Target job title (optional)</label>
          <input
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Senior Product Manager"
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
          />
        </div>

        {error ? <p className="mt-3 text-sm text-rose-500">{error}</p> : null}

        <button
          type="button"
          onClick={analyze}
          disabled={loading}
          className="mt-5 w-full rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-110 disabled:opacity-60"
        >
          {loading ? "Analyzing…" : "Analyze My Resume"}
        </button>
        <p className="mt-3 text-center text-xs text-slate-400 dark:text-slate-500">
          Your resume is analyzed on the fly and never stored.
        </p>
      </div>

      <div>
        {!result && !loading ? (
          <div className="flex h-full min-h-[280px] items-center justify-center rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400 dark:border-slate-800 dark:text-slate-500">
            Your ATS score and findings will appear here.
          </div>
        ) : null}

        {loading ? (
          <div className="flex h-full min-h-[280px] items-center justify-center rounded-2xl border border-slate-200/80 bg-white/80 text-sm text-slate-400 dark:border-slate-800 dark:bg-[#0f172a]/80">
            Reading your resume…
          </div>
        ) : null}

        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white/80 p-5 backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
              <div
                className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
                style={{ backgroundColor: scoreColor(result.score) }}
              >
                {result.score}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">ATS Score</p>
                <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{result.summary}</p>
              </div>
            </div>

            <div className="space-y-2.5">
              {sortedFindings.map((f, i) => (
                <div key={i} className={`rounded-xl border p-4 ${SEVERITY_STYLE[f.severity]}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {CATEGORY_LABELS[f.category] ?? f.category}
                    </span>
                    <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500 dark:bg-white/10 dark:text-slate-400">
                      {f.severity}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm font-medium text-slate-800 dark:text-slate-100">{f.issue}</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Fix: {f.fix}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
